import { z } from 'zod';
import { isIsoDate, nextDay } from '@till-erp/platform/dates';

/**
 * Eingaben und Sätze von Chatter und Wiedervorlage. Ohne Datenbank: Server und Web-App prüfen mit
 * denselben Zod-Schemas (EXPERIENCE §4.3).
 */

export const NOTE_MAX_LENGTH = 5000;
export const TASK_TITLE_MAX_LENGTH = 200;
/** Höchstlänge der Beschreibung einer Aufgabe (Story 23.1; Check in Migration 0503). */
export const TASK_DESCRIPTION_MAX_LENGTH = 5000;
export const FILE_NAME_MAX_LENGTH = 255;
/** Höchstzahl erwähnter Personen je Notiz (technische Grenze gegen Massen-Benachrichtigung). */
export const MENTIONS_MAX = 20;
/** Höchstzahl Einträge, die die Glocke zeigt (neueste zuerst). */
export const NOTIFICATIONS_MAX = 30;

export const COLLAB_TEXTS = {
  noteEmpty: 'Bitte einen Text für die Notiz eingeben.',
  noteTooLong: `Die Notiz ist zu lang (höchstens ${NOTE_MAX_LENGTH.toLocaleString('de-DE')} Zeichen).`,
  titleEmpty: 'Bitte einen Titel eingeben.',
  titleTooLong: `Der Titel ist zu lang (höchstens ${TASK_TITLE_MAX_LENGTH} Zeichen).`,
  assigneeMissing: 'Bitte eine zuständige Person wählen.',
  assigneeInvalid: 'Diese Person gibt es nicht oder sie ist gesperrt. Bitte eine andere Person wählen.',
  dueDateInvalid: 'Bitte ein gültiges Datum wählen.',
  entityTypeUnknown: 'Diese Art von Datensatz hat keinen Chatter.',
  entityNotFound: 'Diesen Datensatz gibt es nicht.',
  forbidden: 'Den Chatter dieses Datensatzes darfst du nicht sehen.',
  emptyDone: 'Keine offenen Aufgaben',
  empty: 'Nichts zu tun. Neue Klärfälle und fällige Wiedervorlagen erscheinen hier automatisch.',
  fileEmpty: 'Die Datei ist leer. Bitte eine Datei mit Inhalt wählen.',
  fileUnreadable: 'Die Datei kam nicht vollständig an. Bitte noch einmal anhängen.',
  fileNameEmpty: 'Die Datei hat keinen Namen. Bitte die Datei umbenennen und noch einmal anhängen.',
  fileNameTooLong: `Der Dateiname ist zu lang (höchstens ${FILE_NAME_MAX_LENGTH} Zeichen). Bitte die Datei umbenennen.`,
  fileStoreMissing:
    'Die Dateiablage ist nicht eingerichtet. Dateien können erst angehängt werden, wenn Rudolf den Schlüssel für Supabase Storage auf dem Server einträgt.',
  fileStoreUnavailable: 'Die Dateiablage antwortet gerade nicht. Bitte in ein paar Minuten noch einmal versuchen.',
  fileStoreRejected: 'Die Dateiablage hat die Datei abgelehnt. Bitte Rudolf ansprechen.',
  chatterLoading: 'Der Chatter lädt noch. Bitte gleich noch einmal versuchen.',
  attachmentNotFound: 'Diese Datei gibt es an diesem Datensatz nicht.',
  filterInvalid: 'Diesen Filter gibt es nicht. Erlaubt sind all, notes, emails und changes.',
  emailsEmpty: 'Noch keine E-Mails. Hier stehen alle E-Mails, die das ERP zu diesem Datensatz sendet.',
  mentionsInvalid: 'Mindestens eine erwähnte Person gibt es nicht oder sie ist gesperrt. Bitte die Erwähnung entfernen oder neu wählen.',
  mentionsTooMany: `Bitte höchstens ${MENTIONS_MAX} Personen in einer Notiz erwähnen.`,
  assigneeRecordIncomplete: 'Bitte Art und Kennung des Datensatzes zusammen angeben.',
  /** Erwähnte Personen ohne Leserecht am Datensatz (Bug 28, Rudolf F2): alle Namen, Satz in Einzahl oder Mehrzahl. */
  mentionsNoAccess: (names: readonly string[]) =>
    names.length === 1
      ? `${names[0]} darf diesen Datensatz nicht sehen und kann hier nicht erwähnt werden.`
      : `${names.join(', ')} dürfen diesen Datensatz nicht sehen und können hier nicht erwähnt werden.`,
  notificationsEmpty: 'Keine Benachrichtigungen. Hier erscheinen @Erwähnungen und Änderungen an Datensätzen, denen du folgst.',
  seenInvalid: 'Diesen Stand der Glocke gibt es nicht. Bitte die Glocke neu öffnen.',
  descriptionTooLong: `Die Beschreibung ist zu lang (höchstens ${TASK_DESCRIPTION_MAX_LENGTH.toLocaleString('de-DE')} Zeichen).`,
  entityIncomplete: 'Bitte Art und Kennung des Datensatzes zusammen angeben – oder beide weglassen (freie Aufgabe).',
  startDateInvalid: 'Bitte ein gültiges Startdatum wählen.',
} as const;

/** Satz „zu groß“ mit der Höchstgröße aus der Einstellung `chatter_attachment_max_mb`. */
export function fileTooLargeText(maxBytes: number): string {
  return `Die Datei ist zu groß (höchstens ${formatMegabytes(maxBytes)} MB).`;
}

/** Bytes → Megabyte (Binärpräfix wie die Einstellung), deutsch, z. B. 26214400 → „25“. */
export function formatMegabytes(bytes: number): string {
  return (bytes / (1024 * 1024)).toLocaleString('de-DE', { maximumFractionDigits: 1 });
}

export const noteBodySchema = z
  .string({ error: COLLAB_TEXTS.noteEmpty })
  .trim()
  .min(1, COLLAB_TEXTS.noteEmpty)
  .max(NOTE_MAX_LENGTH, COLLAB_TEXTS.noteTooLong);

export const taskTitleSchema = z
  .string({ error: COLLAB_TEXTS.titleEmpty })
  .trim()
  .min(1, COLLAB_TEXTS.titleEmpty)
  .max(TASK_TITLE_MAX_LENGTH, COLLAB_TEXTS.titleTooLong);

export const assigneeIdSchema = z.uuid({ error: COLLAB_TEXTS.assigneeMissing });

/** Fälligkeit als fachliches Datum `YYYY-MM-DD` (Europe/Berlin); auch in der Vergangenheit erlaubt. */
export const dueDateSchema = z.string({ error: COLLAB_TEXTS.dueDateInvalid }).refine(isIsoDate, COLLAB_TEXTS.dueDateInvalid);

/**
 * Erwähnte Personen (Benutzer-IDs) einer Notiz; optional, höchstens 20. Doppelte entdoppelt der Server;
 * unbekannte oder gesperrte Personen lehnt er mit 422 `errors.mentions` ab.
 */
export const mentionsSchema = z
  .array(z.string({ error: COLLAB_TEXTS.mentionsInvalid }), { error: COLLAB_TEXTS.mentionsInvalid })
  .max(MENTIONS_MAX, COLLAB_TEXTS.mentionsTooMany);

/** Notiz schreiben (Inhalt der Anfrage). */
export const newNoteSchema = z.object({ body: noteBodySchema, mentions: mentionsSchema.optional() });

/**
 * Erwähnte Personen, deren „@Anzeigename“ noch im Text steht (Web-App: gewählt per @-Auswahl, danach
 * vielleicht wieder gelöscht). Längere Namen zuerst: ein „@Tobias Beispiel“ zählt nicht zugleich für „Tobias“.
 * Reihenfolge wie `candidates`, ohne Doppelte.
 */
export function mentionedIds(body: string, candidates: readonly { id: string; displayName: string }[]): string[] {
  let rest = body;
  const found = new Set<string>();
  for (const c of [...candidates].sort((a, b) => b.displayName.length - a.displayName.length)) {
    const next = consumeMention(rest, c.displayName);
    if (next !== null) {
      found.add(c.id);
      rest = next;
    }
  }
  return [...new Set(candidates.map((c) => c.id))].filter((id) => found.has(id));
}

/**
 * Entfernt jedes „@Name“ aus dem Text, nach dem kein Buchstabe und keine Ziffer folgt (ein „@Tobi“ zählt nicht,
 * wenn „@Tobias“ dasteht); `null`, wenn keins vorkommt.
 */
function consumeMention(body: string, displayName: string): string | null {
  const needle = `@${displayName}`;
  let out = body;
  let hit = false;
  for (let i = out.indexOf(needle); i >= 0; i = out.indexOf(needle, i + 1)) {
    const next = out.charAt(i + needle.length);
    if (next === '' || !/[\p{L}\p{N}]/u.test(next)) {
      out = out.slice(0, i) + ' '.repeat(needle.length) + out.slice(i + needle.length);
      hit = true;
    }
  }
  return hit ? out : null;
}

/** Satz der Glocke für eine Erwähnung, z. B. „Rolf Beispiel hat dich erwähnt“. */
export function mentionedText(authorName: string): string {
  return `${authorName} hat dich erwähnt`;
}

/** Stand der Glocke als gesehen setzen (Inhalt der Anfrage): `upTo` = id des neuesten gezeigten Eintrags. */
export const markSeenSchema = z.object({ upTo: z.uuid({ error: COLLAB_TEXTS.seenInvalid }) });

export const notificationKinds = ['mention', 'change', 'system', 'watch', 'area', 'person'] as const;

/** Bereichs-Hinweise in der Glocke eines Arbeitsbereichs und der Admins (Story 7.17). */
export const AREA_NOTICE_TEXTS = {
  label: 'Hinweis',
} as const;

/** Persönliche Meldungen in der Glocke einer Person (Story 22.6). */
export const PERSON_NOTICE_TEXTS = {
  label: 'Meldung',
} as const;

/** Hinweise zu beobachteten Ansichten (Story 1.34, FR-95). */
export const WATCH_NOTICE_TEXTS = {
  label: 'Beobachtete Ansicht',
  /** „2 neue Artikel in ‚Bald leer‘“; bei einer Zeile „1 Artikel neu in ‚Bald leer‘“ (ohne Geschlecht des Worts). */
  text: (count: number, noun: { one: string; many: string }, viewName: string) =>
    count === 1 ? `1 ${noun.one} neu in ‚${viewName}‘` : `${count.toLocaleString('de-DE')} neue ${noun.many} in ‚${viewName}‘`,
  /** Ansicht gelöscht oder nicht mehr sichtbar: Satz ohne Namen, ohne Link. */
  gone: (count: number, noun: { one: string; many: string }) =>
    count === 1 ? `1 ${noun.one} neu in einer Ansicht, die es nicht mehr gibt` : `${count.toLocaleString('de-DE')} neue ${noun.many} in einer Ansicht, die es nicht mehr gibt`,
  /** Fallback, wenn reporting die Liste nicht (mehr) kennt. */
  rows: { one: 'Zeile', many: 'Zeilen' },
} as const;

/** Wohin der Hinweis zu einer beobachteten Ansicht führt (Web-App: Weiterleitung zur Liste mit Markierung). */
export function watchHitPath(hitId: string): string {
  return `/beobachtet/${hitId}`;
}

// ---------------------------------------------------------------------------
// „Neu im ERP“ (Story 1.99): Neuerungen nach einer Auslieferung

/** Seite „Neu im ERP“ in der Web-App. */
export const RELEASE_NOTES_PATH = '/neu-im-erp';
/** So viele neueste Neuerungen zeigt die Glocke in ihrem Abschnitt „Neu im ERP“ (Anzeige, keine Fachgrenze). */
export const NEWS_IN_BELL_MAX = 5;

/** Ein Eintrag der Datei `apps/server/src/release-notes.ts`: je gemergter Story Ticket, Datum, ein Satz und ein Link. */
export const releaseNoteInputSchema = z.object({
  /** Ticketnummer, z. B. „6.22“ oder „5.12A“. */
  key: z.string().regex(/^[0-9]{1,3}\.[0-9]{1,3}[A-Z]?$/),
  /** Datum des Merges `YYYY-MM-DD` (Europe/Berlin). */
  date: z.string().refine(isIsoDate),
  /** Ein Satz in Alltagssprache, was jetzt neu ist (ohne „Neu:“ davor). */
  text: z.string().trim().min(1).max(200),
  /** Ort in der Web-App, z. B. `/auftraege` oder `/aufgaben?gruppe=cases`. */
  href: z.string().regex(/^\/[a-z0-9/_?=&.-]{0,200}$/),
});
export type ReleaseNoteInput = z.infer<typeof releaseNoteInputSchema>;

export const RELEASE_NOTE_TEXTS = {
  title: 'Neu im ERP',
  /** Satz in der Glocke: „Neu: …“. */
  text: (text: string) => `Neu: ${text}`,
  /** Grau unter dem Satz, z. B. „Story 6.22“. */
  story: (key: string) => `Story ${key}`,
  all: 'Alle Neuigkeiten',
  unread: 'Neu',
  empty: 'Noch keine Neuerungen.',
  intro: 'Was nach den letzten Auslieferungen neu ist – neueste oben. „Neu“ steht an allem, was du in der Glocke noch nicht gesehen hast; nach dem Öffnen der Glocke verschwindet es.',
} as const;
export type NotificationKind = (typeof notificationKinds)[number];

/** Beschreibung einer Aufgabe (Story 23.1): leer oder nur Leerzeichen = keine (`normalizeDescription`); höchstens 5.000 Zeichen. */
export const taskDescriptionSchema = z.string().max(TASK_DESCRIPTION_MAX_LENGTH, COLLAB_TEXTS.descriptionTooLong).nullable();

/** Leere Beschreibung → `null`. */
export function normalizeDescription(value: string | null | undefined): string | null {
  return value == null || value.trim() === '' ? null : value;
}

/** Startdatum einer Aufgabe (Story 23.1); `null` = nicht festgelegt. */
export const startDateSchema = z.string({ error: COLLAB_TEXTS.startDateInvalid }).refine(isIsoDate, COLLAB_TEXTS.startDateInvalid).nullable();

/**
 * Prioritäten einer Aufgabe (Story 23.1, FR-151), niedrig bis dringend; Standard „Normal“ (zeigt die Liste nicht als Pille).
 */
export const taskPriorities = ['low', 'normal', 'high', 'urgent'] as const;
export type TaskPriority = (typeof taskPriorities)[number];

export function isTaskPriority(value: unknown): value is TaskPriority {
  return typeof value === 'string' && (taskPriorities as readonly string[]).includes(value);
}

export const taskPriorityLabels: Record<TaskPriority, string> = {
  low: 'Niedrig',
  normal: 'Normal',
  high: 'Hoch',
  urgent: 'Dringend',
};

/** Texte „Aufgabe aus Notiz“ (Story 23.20, FR-166, FR-63). */
export const NOTE_TASK_TEXTS = {
  /** Eintrag ist keine Notiz (E-Mail, Änderung, Datei, Wiedervorlage) oder gibt es nicht. */
  notANote: 'Eine Aufgabe lässt sich nur aus einer internen Notiz anlegen.',
  /** Häkchen „als Aufgabe“ ohne erwähnte Person. */
  asTaskNoMention: 'Für „als Aufgabe“ bitte mindestens eine Person mit @ erwähnen.',
  /** Häkchen „als Aufgabe“ mit einer Person, die in der Notiz nicht erwähnt ist. */
  asTaskNotMentioned: 'Aufgaben gehen nur an Personen, die in der Notiz erwähnt sind.',
  /** KI legt keine Aufgaben an (AD-16). */
  aiForbidden: 'Die KI legt keine Aufgaben aus Notizen an. Bitte selbst „Als Aufgabe …“ wählen.',
  /** Zuständige fehlt (System ohne Person und ohne Erwähnung). */
  assigneeMissing: COLLAB_TEXTS.assigneeMissing,
  /** Wiederherstellen, obwohl es zur Notiz und Person schon eine neue Aufgabe gibt. */
  restoreTaken: 'Zu dieser Notiz und Person gibt es inzwischen eine neue Aufgabe. Diese bleibt im Papierkorb.',
  /** Band im Detail-Blatt, z. B. „Aus Notiz von Anna Test, 22.02.2027“. */
  fromNote: (author: string, date: string) => `Aus Notiz von ${author}, ${date}`,
  /** Menüpunkt an der Notiz. */
  menuItem: 'Als Aufgabe …',
  /** Häkchen beim Schreiben einer Notiz mit @-Erwähnung. */
  checkbox: 'als Aufgabe',
  /** Pill an der Notiz. */
  pill: 'Aufgabe',
  /** Toast nach dem Anlegen; zweiter Aufruf öffnet die vorhandene. */
  created: 'Aufgabe aus Notiz angelegt',
  existing: 'Zu dieser Notiz gibt es schon eine Aufgabe für diese Person.',
  /** Neu zuweisen an eine Person, die zur selben Notiz schon eine Aufgabe hat. */
  assigneeTaken: (name: string) => `${name} hat zu dieser Notiz schon eine Aufgabe.`,
} as const;

/**
 * Häkchen „als Aufgabe“ beim Schreiben einer Notiz (FR-63, Story 23.20): je gewählter erwähnter Person (`assigneeIds`,
 * Teilmenge der Erwähnten, mindestens eine) genau eine Aufgabe. Fälligkeit und Priorität aus den Chips (sonst die
 * Deutung `buildNoteTaskDraft` des Notiztexts).
 */
export const noteAsTaskSchema = z.object({
  assigneeIds: z
    .array(z.uuid({ error: NOTE_TASK_TEXTS.asTaskNotMentioned }), { error: NOTE_TASK_TEXTS.asTaskNoMention })
    .min(1, NOTE_TASK_TEXTS.asTaskNoMention)
    .max(MENTIONS_MAX, COLLAB_TEXTS.mentionsTooMany),
  dueDate: dueDateSchema.optional(),
  priority: z.enum(taskPriorities).optional(),
});

/** Notiz mit optionalem Häkchen „als Aufgabe“ (Inhalt der Anfrage „Notiz schreiben“). */
export const newNoteWithTaskSchema = newNoteSchema.extend({ asTask: noteAsTaskSchema.optional() });

/**
 * Sichtbarer Status einer Aufgabe (Story 23.1, FR-165): Offen, In Arbeit, Wartet, Erledigt. `status` (offen/erledigt)
 * folgt: nur „Erledigt“ ist `done`.
 */
export const taskStages = ['open', 'in_progress', 'waiting', 'done'] as const;
export type TaskStage = (typeof taskStages)[number];

export const taskStageLabels: Record<TaskStage, string> = {
  open: 'Offen',
  in_progress: 'In Arbeit',
  waiting: 'Wartet',
  done: 'Erledigt',
};

/**
 * Texte des Boards (Story 23.2, EXPERIENCE §4.22): Spaltennamen sind die Status-Wörter (`taskStageLabels`); Ansagen für
 * Hilfsmittel beim Ziehen per Tastatur.
 */
export const TASK_BOARD_TEXTS = {
  tab: 'Board',
  label: 'Board der Aufgaben',
  empty: 'Noch keine Aufgaben. Leg die erste an – sie erscheint hier in „Offen“.',
  emptyColumn: 'Keine Aufgaben',
  dropHere: 'Hierher ziehen',
  moveTo: 'Verschieben nach …',
  moveToLabel: (title: string) => `„${title}“ verschieben nach`,
  moved: (title: string, stage: string) => `„${title}“ → ${stage}`,
  doneHint: (days: number) => `Erledigte der letzten ${days} Tage`,
  person: 'Person',
  everyone: 'Alle Personen',
  priority: 'Priorität',
  allPriorities: 'Alle Prioritäten',
  dueOverdue: (date: string) => `überfällig · ${date}`,
  personInvalid: 'Diese Person gibt es nicht. Bitte den Filter „Person“ neu wählen.',
  priorityInvalid: 'Diese Priorität gibt es nicht. Erlaubt sind Niedrig, Normal, Hoch und Dringend.',
  instructions:
    'Leertaste hebt die Karte an. Die Pfeiltasten wählen die Spalte, Leertaste oder Enter legt ab, Esc bricht ab. Ohne Anheben öffnet Enter die Aufgabe.',
  pickedUp: (title: string, stage: string) => `„${title}“ angehoben, Spalte ${stage}.`,
  over: (title: string, stage: string) => `„${title}“ über Spalte ${stage}.`,
  dropped: (title: string, stage: string) => `„${title}“ liegt jetzt in ${stage}.`,
  cancelled: (title: string) => `Verschieben von „${title}“ abgebrochen.`,
} as const;

/** Längster Zeitraum, den der Kalender auf einmal lädt (6 Wochen Monatsraster, Story 23.6). */
export const TASK_CALENDAR_MAX_DAYS = 42;

/**
 * Texte des Kalenders (Story 23.6, EXPERIENCE §4.22): Monat und Woche, Ziehen auf einen Tag = Verschieben der Fälligkeit
 * (`postponeTask`), Ansagen für Hilfsmittel beim Ziehen per Tastatur.
 */
export const TASK_CALENDAR_TEXTS = {
  tab: 'Kalender',
  label: 'Kalender der Aufgaben',
  month: 'Monat',
  week: 'Woche',
  viewLabel: 'Zeitraum',
  /** Legende unter dem Monat; die Wörter kommen aus `taskPriorityLabels`. */
  legend: 'Legende',
  today: 'Heute',
  prevMonth: 'Voriger Monat',
  nextMonth: 'Nächster Monat',
  prevWeek: 'Vorige Woche',
  nextWeek: 'Nächste Woche',
  overdue: (n: number) => `${n} überfällig`,
  dueIn: (n: number, month: string) => `${n} fällig im ${month}`,
  dueInWeek: (n: number) => `${n} fällig in dieser Woche`,
  moved: (title: string, weekdayDate: string, free: boolean) => `„${title}“ auf ${weekdayDate} verschoben${free ? ' – kein Arbeitstag' : ''}`,
  notWorkday: 'kein Arbeitstag',
  onlyFuture: 'Nur auf morgen oder später',
  rangeInvalid: `Zeitraum ungültig: „from“ und „to“ als Datum (JJJJ-MM-TT), „from“ nicht nach „to“, höchstens ${TASK_CALENDAR_MAX_DAYS} Tage.`,
  more: (n: number) => `+${n} weitere`,
  moreLabel: (n: number, day: string) => `${n} weitere am ${day} – Woche zeigen`,
  newOn: (day: string) => `Neue Aufgabe am ${day}`,
  emptyWeek: 'Diese Woche ist nichts fällig.',
  emptyMonth: 'In diesem Monat ist nichts fällig. Leg eine Aufgabe an oder blättere weiter.',
  emptyDay: 'Nichts fällig',
  changeDate: 'Datum ändern',
  changeDateLabel: (title: string) => `Datum ändern: „${title}“`,
  newDate: 'Neue Fälligkeit',
  save: 'Verschieben',
  quickTomorrow: 'Morgen',
  quickNextMonday: 'Nächster Mo',
  quickNextWeek: '+1 Woche',
  dragHint: 'oder Karte lang drücken und auf einen Tag der Wochenleiste ziehen',
  instructions:
    'Leertaste hebt die Aufgabe an. Pfeil links/rechts wählt den Tag, Pfeil hoch/runter die Woche; Leertaste oder Enter legt ab, Esc bricht ab. Ohne Anheben öffnet Enter die Aufgabe.',
  pickedUp: (title: string, day: string) => `„${title}“ angehoben, ${day}.`,
  over: (title: string, day: string, note: string | null) => `„${title}“ über ${day}${note ? ` – ${note}` : ''}.`,
  dropped: (title: string, day: string) => `„${title}“ liegt jetzt am ${day}.`,
  refused: (title: string) => `„${title}“ bleibt liegen. Nur auf morgen oder später.`,
  cancelled: (title: string) => `Verschieben von „${title}“ abgebrochen.`,
} as const;

/**
 * Längster Zeitraum, den die Zeitleiste auf einmal lädt (Story 23.8): Abfragefenster (Quartal = 13 Wochen + Puffer),
 * keine fachliche Frist.
 */
export const TASK_TIMELINE_MAX_DAYS = 98;

/**
 * Texte der Zeitleiste (Story 23.8, FR-156, EXPERIENCE §4.22): Balken Start → Fällig je Projekt; Ziehen ändert Start und
 * Fällig in einem Zug (`collab.setTaskDates`), Rückgängig mit den vorigen Werten.
 */
export const TASK_TIMELINE_TEXTS = {
  tab: 'Zeitleiste',
  label: 'Zeitleiste der Aufgaben',
  zoomLabel: 'Zeitraum',
  week: 'Woche',
  month: 'Monat',
  quarter: 'Quartal',
  today: 'Heute',
  prev: 'Früher',
  next: 'Später',
  taskColumn: 'Aufgabe',
  noProject: 'Ohne Projekt',
  progress: (done: number, total: number) => `${done} von ${total} erledigt`,
  target: (date: string) => `Ziel ${date}`,
  overdue: (n: number) => `${n} überfällig`,
  openIn: (n: number, projects: number) => `${n} offen in ${projects} ${projects === 1 ? 'Projekt' : 'Projekten'}`,
  empty: 'Keine offenen Aufgaben in diesem Zeitraum.',
  emptyHint: 'Legen Sie oben eine an oder springen Sie zu „Heute“.',
  stillSaving: 'Einen Moment – die letzte Änderung dieser Aufgabe wird noch gespeichert. Bitte noch einmal ziehen.',
  emptySearch: (query: string) => `Keine Aufgabe passt zu „${query}“.`,
  newTask: '+ Aufgabe',
  search: 'Aufgaben suchen',
  searchPlaceholder: 'Titel suchen …',
  startAfterDue: 'Start muss vor oder am Fälligkeitstag liegen',
  rangeInvalid: `Zeitraum ungültig: „from“ und „to“ als Datum (JJJJ-MM-TT), „from“ nicht nach „to“, höchstens ${TASK_TIMELINE_MAX_DAYS} Tage.`,
  /** Blase beim Ziehen, z. B. „Sa 27.02. – Mo 08.03. · 10 Tage“. */
  bubble: (start: string, due: string, days: number) => `${start} – ${due} · ${days} ${days === 1 ? 'Tag' : 'Tage'}`,
  startOnWeekend: 'Start auf Wochenende',
  startOnHoliday: (name: string) => `Start auf Feiertag (${name})`,
  moved: (title: string, start: string, due: string) => `„${title}“: ${start === due ? due : `${start} – ${due}`}`,
  barLabel: (title: string, range: string) => `${title}, ${range}`,
  instructions:
    'Pfeil links/rechts verschiebt Start und Fällig um einen Tag, mit Umschalt nur Fällig, mit Alt nur Start. Enter öffnet die Aufgabe.',
  announce: (title: string, range: string) => `„${title}“ jetzt ${range}.`,
  overdueTag: 'überfällig',
  tasksCount: (n: number) => `${n} ${n === 1 ? 'Aufgabe' : 'Aufgaben'}`,
  sheetTitle: (title: string) => `Zeitraum: „${title}“`,
  start: 'Start',
  due: 'Fällig',
  plusDay: '+1 Tag',
  plusWeek: '+1 Woche',
  nextMonday: 'Nächster Mo',
  shiftHint: 'Die Knöpfe verschieben Start und Fällig gemeinsam, die Dauer bleibt.',
  save: 'Übernehmen',
  openTask: 'Aufgabe öffnen',
  noStart: 'ohne Start',
} as const;

/**
 * Texte der Arbeitslast (Story 23.9, FR-157, EXPERIENCE §4.22): je Person offen, überfällig, fällig diese Woche, davon
 * Dringend/Hoch und Balken der nächsten 4 Wochen. Abwesenheit nur als „abwesend bis …“ ohne Art (aus staff, apps/web).
 */
export const TASK_WORKLOAD_TEXTS = {
  tab: 'Arbeitslast',
  label: 'Arbeitslast je Person',
  title: 'Arbeitslast',
  person: 'Person',
  open: 'Offen',
  overdue: 'Überfällig',
  thisWeek: 'Diese Woche',
  urgentHigh: 'davon Dringend/Hoch',
  weeks: 'Nächste 4 Wochen',
  openShort: 'offen',
  overdueShort: 'überfällig',
  thisWeekShort: 'diese Woche',
  urgentHighShort: 'Dringend/ Hoch',
  openWhat: 'offene Aufgaben',
  overdueWhat: 'überfällige Aufgaben',
  thisWeekWhat: 'Aufgaben fällig diese Woche',
  urgentHighWhat: 'dringende oder hohe Aufgaben',
  /** Abwesenheit ohne Art und Grund (aus staff): „abwesend bis 26.02.“ bzw. bei offener Krankheit ohne erfundenes Ende. */
  absentUntil: (dayMonth: string) => `abwesend bis ${dayMonth}`,
  absentOpenEnd: 'abwesend, Ende offen',
  /** Abschlusszeile: Personen ohne offene Aufgaben, je Name ein Sprung in die Liste. */
  idle: 'Ohne offene Aufgaben – hat Luft:',
  idleJump: (name: string) => `Aufgaben von ${name} in der Liste zeigen`,
  search: 'Person suchen …',
  searchLabel: 'Person suchen',
  noMatch: (q: string) => `Niemand passt zu „${q}“.`,
  empty: 'Noch keine aktiven Personen. Lege Benutzer unter Einstellungen › Benutzer an.',
  summary: (open: number) => `${open} offen`,
  summaryOverdue: (overdue: number) => `${overdue} überfällig`,
  week: (range: string) => `Woche ${range}`,
  /** Säule einer Woche (Tooltip und Hilfsmittel): „KW 8: 1 Aufgabe fällig, abwesend Mo–Fr“. */
  weekBar: (label: string, n: number, absentSpan: string) =>
    `${label}: ${n === 0 ? 'keine Aufgabe' : n === 1 ? '1 Aufgabe' : `${n} Aufgaben`} fällig${absentSpan ? `, abwesend ${absentSpan}` : ''}`,
  weekdays: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
  jump: (count: number, what: string, name: string) => `${count} ${what} von ${name} in der Liste zeigen`,
  /** Handy-Karte: an die sichtbare Zahl mit Kurzwort gehängt (Name enthält den sichtbaren Text, WCAG 2.5.3). */
  jumpCard: (name: string) => ` von ${name} in der Liste zeigen`,
} as const;

/** Wiedervorlage anlegen (Inhalt der Anfrage); Priorität, Beschreibung und Start optional (Story 23.1). */
export const newTaskSchema = z.object({
  title: taskTitleSchema,
  assigneeId: assigneeIdSchema,
  dueDate: dueDateSchema,
  priority: z.enum(taskPriorities).default('normal'),
  description: taskDescriptionSchema.optional(),
  startDate: startDateSchema.optional(),
});

/**
 * Arten von Systemhinweisen (Story 2.9; `ai_budget_warning` Story 2.21: KI-Budget zu 80 % erreicht; `heartbeat_warning`
 * Story 1.147: Gegenstelle meldet sich länger nicht als ihr Mindest-Meldetakt; `counterpart_outage` Story 1.115: Beginn
 * und Ende einer Störung je Gegenstelle).
 */
export const systemNoticeKinds = ['quota_warning', 'ai_budget_warning', 'mcp_expiry_warning', 'heartbeat_warning', 'counterpart_outage'] as const;
export type SystemNoticeKind = (typeof systemNoticeKinds)[number];

/**
 * Pfade der Oberfläche, an die ein Systemhinweis führt (Web-App `/einstellungen/<hauptpunkt>/<reiter>`, Story 1.41;
 * gespeicherte Hinweise mit alten Pfaden leitet die Web-App um).
 */
export const SYSTEM_NOTICE_PATHS = {
  /** Einstellungen › E-Mail & Aufgaben › Postausgang. */
  mail: '/einstellungen/e-mail/postausgang',
  /** Einstellungen › Schnittstellen › Übersicht. */
  counterparts: '/einstellungen/schnittstellen/uebersicht',
  /** Einstellungen › KI › Budget (Story 2.21). */
  aiBudget: '/einstellungen/ki/budget',
} as const;

export const SYSTEM_NOTICE_TEXTS = {
  label: 'Systemhinweis',
  /** „E-Mail-Tagesgrenze zu 80 % erreicht (1.600 von 2.000)“ – Glocke und Betreff der Hinweis-E-Mail. */
  quotaWarning: (name: string, percent: number, used: number, limit: number) =>
    `${name} zu ${percent.toLocaleString('de-DE')} % erreicht (${used.toLocaleString('de-DE')} von ${limit.toLocaleString('de-DE')})`,
  posted: 'Systemhinweis erstellt',
} as const;

export const chatterEntryKinds = ['note', 'task_created'] as const;
export type ChatterEntryKind = (typeof chatterEntryKinds)[number];

/**
 * Arten der Einträge, die der Chatter zeigt: eigene Einträge, Dateien, Änderungen aus dem Protokoll und
 * E-Mails (Quelle meldet das Modul `mail` an, Story 2.2).
 */
export const chatterItemKinds = ['note', 'task_created', 'attachment', 'change', 'email'] as const;
export type ChatterItemKind = (typeof chatterItemKinds)[number];

// ---------------------------------------------------------------------------
// E-Mails im Chatter (Story 2.2; Daten liefert das Modul `mail`)

/** Herkunft einer E-Mail: automatisch über einen Anlass oder von Hand (aus dem Datensatz geschrieben oder erneut gesendet). */
export const emailOrigins = ['auto', 'manual'] as const;
export type EmailOrigin = (typeof emailOrigins)[number];

/** Zustand des Versands, wie Chatter und Versandliste ihn zeigen. */
export const emailStatuses = [
  'sending',
  'waiting_unreachable',
  'waiting_throttled',
  'waiting_quota',
  'sent',
  'failed',
  'shadowed',
  'skipped',
  /** Rückläufer oder Ablehnung der Adresse (Story 2.8); `detail` = Grund, z. B. „Adresse unbekannt“. */
  'bounced',
  /** Werbe-Mail nicht gesendet, weil der Kunde sich nach dem Einreihen abgemeldet hat (Story 2.29). */
  'unsubscribed',
] as const;
export type EmailStatus = (typeof emailStatuses)[number];

/** E-Mail im Chatter eines Datensatzes. */
export interface ChatterEmailView {
  id: string;
  subject: string;
  recipient: string;
  origin: EmailOrigin;
  status: EmailStatus;
  /** Zeitpunkt des Versands (`sent`) bzw. des Endzustands; sonst `null`. */
  statusAt: Date | null;
  /** Grund als deutscher Satz (Fehlschlag); sonst `null`. */
  detail: string | null;
  /** Erneut gesendet von dieser E-Mail; `null` bei automatischen. */
  resendOf: string | null;
  /** Text der E-Mail (Schnappschuss beim Einstellen). */
  body: string;
  /** Sprache der E-Mail; `null` bei automatischen E-Mails ohne Angabe. */
  language: EmailLanguage | null;
  /** Benutzte Vorlage mit Name und Version beim Schreiben (Schnappschuss); `null` = ohne Vorlage. */
  template: { id: string; name: string; version: number } | null;
  /**
   * Anhänge der E-Mail (Kopien der Dateien im Chatter bzw. der Belege aus dem Archiv), in der Reihenfolge der E-Mail.
   * `sha256` = Prüfsumme des Inhalts; `document` = Beleg (Nummer, Fassung), aus dessen Archiv-Artefakt der Anhang
   * stammt (Story 3.10), sonst `null`.
   */
  attachments: { fileName: string; contentType: string; sizeBytes: number; sha256: string; document: { number: string; versionNo: number } | null }[];
}

/** Sprache einer E-Mail aus dem Datensatz (Vorlagen DE und EN, Story 2.7). */
export const emailLanguages = ['de', 'en'] as const;
export type EmailLanguage = (typeof emailLanguages)[number];
export const emailLanguageCodes: Record<EmailLanguage, string> = { de: 'DE', en: 'EN' };

/** „Vorlage Kurze Rückfrage · Version 1 · EN“ (Chatter, Story 2.7); ohne Vorlage nur die Sprache oder `null`. */
export function emailTemplateText(template: { name: string; version: number } | null, language: EmailLanguage | null): string | null {
  const lang = language ? emailLanguageCodes[language] : null;
  if (!template) return lang ? `ohne Vorlage · ${lang}` : null;
  return [`Vorlage ${template.name}`, `Version ${template.version}`, ...(lang ? [lang] : [])].join(' · ');
}

/** „automatisch“ oder „von Hand: Sabine Beispiel“ (R-30). */
export function emailOriginText(origin: EmailOrigin, personName: string): string {
  return origin === 'manual' ? `von Hand: ${personName}` : 'automatisch';
}

/** Zustand als kurzer Satz; `time` ist die Uhrzeit (bzw. Datum und Uhrzeit) des Versands, z. B. „10:42“. */
export function emailStatusText(status: EmailStatus, opts: { time?: string | null; detail?: string | null } = {}): string {
  switch (status) {
    case 'sent':
      return opts.time ? `gesendet ${opts.time}` : 'gesendet';
    case 'sending':
      return 'wartet – wird gesendet';
    case 'waiting_unreachable':
      return 'wartet – Microsoft 365 nicht erreichbar';
    case 'waiting_throttled':
      return 'wartet – Drosselung, geht in der nächsten Minute raus';
    case 'waiting_quota':
      return 'wartet – Tagesgrenze erreicht, geht ab 0:00 Uhr raus';
    case 'shadowed':
      return 'Probe – würde senden';
    case 'skipped':
      return 'nicht gesendet – Betriebsschalter aus';
    case 'failed':
      return opts.detail ? `fehlgeschlagen: ${opts.detail}` : 'fehlgeschlagen';
    case 'bounced':
      return opts.detail ? `E-Mail nicht zustellbar: ${opts.detail}` : 'E-Mail nicht zustellbar';
    case 'unsubscribed':
      return 'nicht gesendet – abgemeldet';
  }
}

/**
 * Kurzer Grund für die Spalte „Grund“ der Versandliste (Ticket 1.186, [RUDOLF] 14.10.2026): nur für E-Mails, die noch
 * nicht fertig sind oder nur zur Probe bzw. wegen des Schalters nicht gesendet wurden; bei erledigten (gesendet,
 * fehlgeschlagen, Rückläufer, abgemeldet) `null` = Spalte leer. Der Text hängt nur am Zustand (keine Personendaten,
 * keine Zugangsdaten). „wartet auf ältere Nachricht“ gibt es hier nicht: Dieser Zustand ist im Ledger nicht sichtbar
 * (die Reihenfolge-Rückstellung setzt den Eintrag zurück auf `queued`).
 */
export function emailWaitReasonText(status: EmailStatus): string | null {
  switch (status) {
    case 'sending':
      return 'wird gerade gesendet';
    case 'waiting_throttled':
      return 'wartet auf Drosselung';
    case 'waiting_unreachable':
      return 'Gegenstelle nicht erreichbar';
    case 'waiting_quota':
      return 'wartet auf Tagesgrenze';
    case 'shadowed':
      return 'Probe-Modus – wird nicht gesendet';
    case 'skipped':
      return 'Betriebsschalter aus';
    case 'sent':
    case 'failed':
    case 'bounced':
    case 'unsubscribed':
      return null;
  }
}

/**
 * Filter des Chatters (EXPERIENCE §4.1): Alles · Notizen (Notizen, Dateien, Wiedervorlagen) ·
 * E-Mails (Modul `mail`) · Änderungen (Protokoll des Datensatzes).
 */
export const chatterFilters = ['all', 'notes', 'emails', 'changes'] as const;
export type ChatterFilter = (typeof chatterFilters)[number];

export const chatterFilterLabels: Record<ChatterFilter, string> = {
  all: 'Alles',
  notes: 'Notizen',
  emails: 'E-Mails',
  changes: 'Änderungen',
};

// ---------------------------------------------------------------------------
// Dateien im Chatter

/** Dateiname ohne Pfad und Steuerzeichen, Leerraum am Rand entfernt. */
export function cleanFileName(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? '';
  // eslint-disable-next-line no-control-regex -- Steuerzeichen bewusst entfernen
  return base.replace(/[\u0000-\u001f\u007f]/g, '').trim();
}

/**
 * Prüft Name und Größe einer Datei vor dem Speichern (Server und Web-App mit derselben Regel).
 * `maxBytes` kommt aus der Einstellung `chatter_attachment_max_mb`. Leeres Ergebnis = in Ordnung.
 */
export function checkAttachment(file: { fileName: string; sizeBytes: number }, maxBytes: number): Record<string, string> {
  const errors: Record<string, string> = {};
  const name = cleanFileName(file.fileName);
  if (name.length === 0) errors.fileName = COLLAB_TEXTS.fileNameEmpty;
  else if (name.length > FILE_NAME_MAX_LENGTH) errors.fileName = COLLAB_TEXTS.fileNameTooLong;
  if (!Number.isSafeInteger(file.sizeBytes) || file.sizeBytes <= 0) errors.file = COLLAB_TEXTS.fileEmpty;
  else if (file.sizeBytes > maxBytes) errors.file = fileTooLargeText(maxBytes);
  return errors;
}

const CONTENT_TYPE = /^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*$/;
export const FALLBACK_CONTENT_TYPE = 'application/octet-stream';

/** Medientyp aus dem Browser vereinheitlichen (klein, ohne Parameter); Unbekanntes → `application/octet-stream`. */
export function normalizeContentType(value: string | null | undefined): string {
  const type = (value ?? '').split(';')[0]!.trim().toLowerCase();
  return CONTENT_TYPE.test(type) && type.length <= 255 ? type : FALLBACK_CONTENT_TYPE;
}

/** Arten, die der Browser gefahrlos selbst anzeigt (keine Skripte). Alles andere wird nur heruntergeladen. */
const SAFE_TO_OPEN = new Set(['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'text/plain']);
/** Endungen, die ein Browser als Seite oder Skript ausführen könnte – nie öffnen, auch bei harmlosem Typ. */
const ACTIVE_EXTENSIONS = new Set(['html', 'htm', 'xhtml', 'xht', 'shtml', 'svg', 'svgz', 'xml', 'xsl', 'js', 'mjs', 'hta', 'mht', 'mhtml']);

/**
 * Darf die Datei im Browser geöffnet werden? Nur bekannte harmlose Arten (PDF, Bilder, Text) und nie
 * Dateien mit Endung einer Webseite oder eines Skripts (EXPERIENCE: HTML/Skripte nie in der ERP-Herkunft öffnen).
 */
export function isSafeToOpen(fileName: string, contentType: string): boolean {
  const ext = fileName.includes('.') ? fileName.split('.').pop()!.toLowerCase() : '';
  return SAFE_TO_OPEN.has(normalizeContentType(contentType)) && !ACTIVE_EXTENSIONS.has(ext);
}

export const taskStatuses = ['open', 'done'] as const;
export type TaskStatus = (typeof taskStatuses)[number];

// ---------------------------------------------------------------------------
// Aufgabenseite (Story 2.4, EXPERIENCE §4.2)

/**
 * Gruppen der Aufgabenseite in fester Reihenfolge (nach „Klärfälle“). Wiedervorlagen von Hand stehen immer in
 * „Wiedervorlagen“; Module wählen die Gruppe beim Anlegen. Gleiche Liste im Event-Vertrag `collab.TaskCreated`.
 */
export const taskGroups = ['approvals', 'shipping', 'money', 'follow_up'] as const;
export type TaskGroup = (typeof taskGroups)[number];

export const taskGroupLabels: Record<TaskGroup, string> = {
  approvals: 'Freigaben',
  shipping: 'Versand',
  money: 'Geld',
  follow_up: 'Wiedervorlagen',
};

export const TASK_KEY_MAX_LENGTH = 300;
export const TASK_CLOSE_NOTE_MAX_LENGTH = 200;

export const TASK_TEXTS = {
  notFound: 'Diese Wiedervorlage gibt es nicht.',
  /** Story 23.15: Die vorgegebene Kennung ist schon vergeben (z. B. KI-Vorschlag schon übernommen). */
  idTaken: 'Diese Aufgabe ist schon angelegt.',
  stale: 'Die Wiedervorlage wurde inzwischen geändert. Die Liste zeigt jetzt den neuen Stand.',
  alreadyDone: 'Diese Wiedervorlage ist schon erledigt.',
  notDone: 'Diese Wiedervorlage ist offen.',
  closedAutomatically: 'Diese Wiedervorlage wurde automatisch geschlossen und lässt sich nicht wieder öffnen.',
  dueDateNotFuture: 'Bitte ein Datum ab morgen wählen.',
  restoreInvalid: 'Rückgängig geht nur auf die Fälligkeit vor dem letzten Verschieben.',
  reopenedElsewhere: 'Für diesen Vorgang gibt es schon eine neue offene Wiedervorlage. Diese bleibt erledigt.',
  ownerOnly: 'Nur das Modul, das die Wiedervorlage angelegt hat, darf sie per Schlüssel schließen.',
  keyInvalid: 'Der Schlüssel der Wiedervorlage ist ungültig (Form <zweck>:<art>:<kennung>).',
  caseStale: 'Der Klärfall wurde inzwischen geändert. Die Liste zeigt jetzt den neuen Stand.',
  caseNotOpen: 'Dieser Klärfall ist schon gelöst.',
  completed: 'Wiedervorlage erledigt',
  reopened: 'Wiedervorlage wieder offen',
  postponed: (date: string) => `Wiedervorlage verschoben auf ${date}`,
  assigned: (name: string) => `Wiedervorlage zugewiesen an ${name}`,
  takenOver: 'Wiedervorlage übernommen',
  caseTakenOver: 'Klärfall übernommen',
  undone: 'Rückgängig gemacht',
  /** Freie Aufgabe (Story 23.1). */
  created: 'Aufgabe angelegt',
  /** Toast nach dem Anlegen mit späterer Fälligkeit (Liste zeigt bis heute fällige), Datum „TT.MM.“. */
  createdLater: (date: string) => `Aufgabe angelegt. Erscheint ab ${date}`,
  stageSet: (label: string) => `Status „${label}“`,
  prioritySet: (label: string) => `Priorität „${label}“`,
  titleSet: 'Titel geändert',
  descriptionSet: 'Beschreibung gespeichert',
  descriptionCleared: 'Beschreibung entfernt',
  startSet: (date: string) => `Start ${date}`,
  startCleared: 'Start entfernt',
  dueSet: (date: string) => `Fällig ${date}`,
  assigneeSet: (name: string) => `Zuständig: ${name}`,
  freeTask: 'Freie Aufgabe',
  /** Karte der freien Aufgaben auf der Aufgabenseite (nicht unter „Wiedervorlagen“). */
  freeTasksGroup: 'Aufgaben',
  newTask: 'Neue Aufgabe',
  stageStatusMismatch: 'Status und Erledigt passen nicht zusammen. Bitte nur einen von beiden angeben.',
  /** Glocken-Meldung an die neue zuständige Person (Titel höchstens 200 Zeichen, Satz höchstens 300). */
  assignedNotice: (title: string) => `Neue Aufgabe für dich: „${title}“`,
  /**
   * Story 23.17: Die Rolle „Mitarbeiter“ liest keine Datensätze und legt deshalb keine Aufgabe mit Bezug an.
   */
  recordNotReadable: 'Aufgaben mit Bezug auf einen Datensatz kannst du nicht anlegen – dafür fehlt dir das Leserecht.',
} as const;

/**
 * Gesperrter Bezug (Story 23.17): Plural der Datensatz-Art im Hinweis „Nur Nummer sichtbar – Inhalte brauchen Leserecht
 * für …“. Unbekannte Arten: „diesen Datensatz“.
 */
export const LOCKED_RECORD_PLURALS: Readonly<Record<string, string>> = {
  order: 'Aufträge',
  customer: 'Kunden',
  article: 'Artikel',
  supplier: 'Lieferanten',
};

/** Hinweis am Schloss eines gesperrten Bezugs (Tooltip und Screenreader). */
export function lockedRecordHint(entityType: string | null): string {
  const plural = entityType === null ? undefined : LOCKED_RECORD_PLURALS[entityType];
  return `Nur Nummer sichtbar – Inhalte brauchen Leserecht für ${plural ?? 'diesen Datensatz'}`;
}

/** Höchstzahl Aufgaben je Löschen bzw. Wiederherstellen (Papierkorb, [RUDOLF] 29.10.2026); technische Grenze. */
export const TASK_TRASH_MAX = 100;

/** Texte zum Löschen mit Papierkorb ([RUDOLF] 29.10.2026). */
export const TASK_TRASH_TEXTS = {
  noneChosen: 'Bitte mindestens eine Aufgabe wählen.',
  tooMany: `Höchstens ${TASK_TRASH_MAX} Aufgaben auf einmal.`,
  creatorOrAdmin: 'Löschen dürfen nur die Person, die die Aufgabe angelegt hat, und Admins.',
  systemAdminOnly: 'Diese Aufgabe hat das ERP angelegt. Löschen dürfen sie nur Admins.',
  keyOpenAgain: 'Für diesen Vorgang gibt es inzwischen eine neue offene Wiedervorlage. Diese bleibt im Papierkorb.',
  /** Menüpunkt und Knopf. */
  delete: 'Löschen',
  restore: 'Wiederherstellen',
  tab: 'Papierkorb',
  /** Toast nach dem Löschen (10 s Rückgängig). */
  /** Ohne bekannte Frist (Papierkorb nicht geladen) nur „gelöscht“ – nie eine erfundene Zahl. */
  deleted: (n: number, days: number | null) => `${n === 1 ? 'Aufgabe gelöscht' : `${n} Aufgaben gelöscht`}${days === null ? '' : ` · ${days} Tage im Papierkorb`}`,
  /** Löschen traf nur schon Gelöschte (z. B. zweites Fenster): keine Erfolgsmeldung, kein Rückgängig. */
  alreadyDeleted: 'Schon gelöscht – liegt bereits im Papierkorb.',
  /** Frist abgelaufen (Zeitjob noch nicht gelaufen): nicht mehr wiederherstellbar. */
  expired: (days: number) => `Länger als ${days} Tage im Papierkorb – lässt sich nicht mehr wiederherstellen.`,
  restored: (n: number) => (n === 1 ? 'Aufgabe wiederhergestellt' : `${n} Aufgaben wiederhergestellt`),
  /** Sicherheitsfrage beim Löschen mehrerer. */
  confirmTitle: (n: number) => (n === 1 ? 'Aufgabe löschen?' : `${n} Aufgaben löschen?`),
  confirmBody: (days: number) => `Sie liegen dann ${days} Tage im Papierkorb und lassen sich bis dahin wiederherstellen.`,
  /** Frist für die Sicherheitsfrage nicht geladen: Bestätigen bleibt aus, bis sie da ist. */
  confirmLoading: 'Frist wird geladen …',
  confirmLoadError: 'Die Frist des Papierkorbs ließ sich nicht laden. Löschen geht erst, wenn sie bekannt ist.',
  retry: 'Erneut laden',
  /** Papierkorb. */
  purgeOn: (date: string) => `endgültig weg am ${date}`,
  /** Restzeit bis zum endgültigen Entfernen. */
  left: (days: number) => (days <= 0 ? 'heute endgültig weg' : days === 1 ? 'noch 1 Tag' : `noch ${days} Tage`),
  deletedBy: (name: string | null, when: string) => (name ? `gelöscht von ${name}, ${when}` : `gelöscht ${when}`),
  groups: { today: 'Heute', week: 'Letzte 7 Tage', older: 'Älter' },
  emptyTitle: 'Der Papierkorb ist leer',
  emptyBody: (days: number) => `Gelöschte Aufgaben liegen hier ${days} Tage und lassen sich wiederherstellen.`,
  hint: (days: number) => `Gelöschte Aufgaben bleiben ${days} Tage wiederherstellbar, danach sind sie endgültig weg. Im Protokoll bleibt ein Vermerk.`,
} as const;

// ---------------------------------------------------------------------------
// Erinnerung vor Fälligkeit über die Glocke (Story 23.13, FR-162, EXPERIENCE §6.5)

const REMINDER_WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'] as const;

/** Kurzer Wochentag + Datum, z. B. „Mo 01.03.“ (rein, ohne Uhr; `date` = `YYYY-MM-DD`). */
export function reminderDayLabel(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const weekday = REMINDER_WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? '';
  return `${weekday} ${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.`;
}

/** Längster Titel in der Erinnerung (Satz der Glocke höchstens 300 Zeichen). */
const REMINDER_TITLE_MAX = 200;

/**
 * Satz der Glocken-Erinnerung (Story 23.13). `remindDate` = Tag der Erinnerung (Europe/Berlin).
 * Gleicher Tag → „Heute fällig: …“, nächster Kalendertag → „Morgen fällig (Di 02.03.): …“, sonst „Fällig Mo 01.03.: …“.
 */
export function taskReminderText(title: string, dueDate: string, remindDate: string): string {
  const shown = title.length > REMINDER_TITLE_MAX ? `${title.slice(0, REMINDER_TITLE_MAX - 1)}…` : title;
  if (dueDate === remindDate) return `Heute fällig: ${shown}`;
  if (dueDate === nextDay(remindDate)) return `Morgen fällig (${reminderDayLabel(dueDate)}): ${shown}`;
  return `Fällig ${reminderDayLabel(dueDate)}: ${shown}`;
}

/** Vorlauf in Worten, z. B. „1 Arbeitstag vorher“, „2 Arbeitstage vorher“, „am Fälligkeitstag“. */
export function taskReminderLeadText(workdays: number): string {
  if (workdays === 0) return 'am Fälligkeitstag';
  return `${workdays} ${workdays === 1 ? 'Arbeitstag' : 'Arbeitstage'} vorher`;
}

/** Schlüssel einer Wiedervorlage eines Moduls: `<zweck>:<entity_type>:<entity_id>`. */
export const TASK_KEY_PATTERN = /^[a-z][a-z0-9_]*:[a-z][a-z0-9_]*:\S+$/;

/** Wiedervorlage verschieben (Inhalt der Anfrage): neue Fälligkeit und gelesene Version. */
export const postponeTaskSchema = z.object({ dueDate: dueDateSchema, version: z.number().int().min(1) });

/** Wiedervorlage zuweisen: Person oder `null` (zurück an den Bereich) und gelesene Version. */
export const assignTaskSchema = z.object({
  assigneeId: z.uuid({ error: COLLAB_TEXTS.assigneeMissing }).nullable(),
  version: z.number().int().min(1),
});

/** Erledigen und wieder öffnen: nur die gelesene Version. */
export const taskVersionSchema = z.object({ version: z.number().int().min(1) });

// ---------------------------------------------------------------------------
// Checkliste (Story 23.3, FR-152) und Labels (Story 23.4, FR-153)

export const CHECKLIST_ITEM_MAX_LENGTH = 500;

export const CHECKLIST_TEXTS = {
  textEmpty: 'Bitte einen Text eingeben.',
  textTooLong: `Bitte höchstens ${CHECKLIST_ITEM_MAX_LENGTH} Zeichen eingeben.`,
  notFound: 'Diesen Punkt der Checkliste gibt es nicht.',
  /** Fortschritt, z. B. „2 von 5“. */
  progress: (done: number, total: number) => `${done} von ${total}`,
  /** Hinweis im Blatt einer erledigten Aufgabe mit offenen Punkten. */
  openLeft: (n: number) => (n === 1 ? '1 Punkt noch offen' : `${n} Punkte noch offen`),
  section: 'Checkliste',
  add: 'Punkt hinzufügen',
} as const;

/** Text eines Checklisten-Punkts: getrimmt, 1–500 Zeichen. */
export const checklistTextSchema = z
  .string({ error: CHECKLIST_TEXTS.textEmpty })
  .trim()
  .min(1, CHECKLIST_TEXTS.textEmpty)
  .max(CHECKLIST_ITEM_MAX_LENGTH, CHECKLIST_TEXTS.textTooLong);

/** Arten einer Änderung an der Checkliste (Event `collab.TaskChecklistChanged`). */
export const checklistChanges = ['added', 'checked', 'unchecked', 'renamed', 'removed', 'moved'] as const;
export type ChecklistChange = (typeof checklistChanges)[number];

/**
 * Farben der Labels (Story 23.4): feste Liste, abgebildet auf Tokens der Oberfläche. Rot und Bernstein bewusst nicht
 * wählbar – sie sind das Signal für Dringend/Hoch/Prüfen (DESIGN.md).
 */
export const taskLabelColors = ['gray', 'blue', 'navy', 'green'] as const;
export type TaskLabelColor = (typeof taskLabelColors)[number];

export const taskLabelColorLabels: Record<TaskLabelColor, string> = {
  gray: 'Grau',
  blue: 'Blau',
  navy: 'Dunkelblau',
  green: 'Grün',
};

export const TASK_LABEL_NAME_MAX_LENGTH = 40;

export const TASK_LABEL_TEXTS = {
  nameEmpty: 'Bitte einen Namen für das Label eingeben.',
  nameTooLong: `Bitte höchstens ${TASK_LABEL_NAME_MAX_LENGTH} Zeichen eingeben.`,
  colorInvalid: 'Diese Farbe gibt es nicht. Erlaubt sind Grau, Blau, Dunkelblau und Grün.',
  duplicate: (name: string) => `Das Label „${name}“ gibt es schon.`,
  notFound: 'Dieses Label gibt es nicht.',
  unknownLabels: 'Mindestens ein Label gibt es nicht (mehr). Bitte die Auswahl neu öffnen.',
  stale: 'Das Label wurde inzwischen geändert. Die Liste zeigt jetzt den neuen Stand.',
  adminOnly: 'Nur der Admin kann Labels ändern.',
  filterInvalid: 'Dieses Label gibt es nicht. Bitte den Filter „Label“ neu wählen.',
  filter: 'Label',
  allLabels: 'Alle Labels',
} as const;

/** Name eines Labels: getrimmt, 1–40 Zeichen. */
export const taskLabelNameSchema = z
  .string({ error: TASK_LABEL_TEXTS.nameEmpty })
  .trim()
  .min(1, TASK_LABEL_TEXTS.nameEmpty)
  .max(TASK_LABEL_NAME_MAX_LENGTH, TASK_LABEL_TEXTS.nameTooLong);

export const taskLabelColorSchema = z.enum(taskLabelColors, { error: TASK_LABEL_TEXTS.colorInvalid });

// ---------------------------------------------------------------------------
// Projekte (Story 23.7): Sammelmappe mit Ziel-Datum und Fortschritt

/** Zustand eines Projekts: aktiv oder abgeschlossen (Abschließen lässt offene Aufgaben offen). */
export const taskProjectStates = ['active', 'closed'] as const;
export type TaskProjectState = (typeof taskProjectStates)[number];

export const TASK_PROJECT_NAME_MAX_LENGTH = 80;
export const TASK_PROJECT_DESCRIPTION_MAX_LENGTH = 2000;

export const TASK_PROJECT_TEXTS = {
  nameEmpty: 'Bitte einen Namen für das Projekt eingeben.',
  nameTooLong: `Bitte höchstens ${TASK_PROJECT_NAME_MAX_LENGTH} Zeichen eingeben.`,
  descriptionTooLong: `Bitte höchstens ${TASK_PROJECT_DESCRIPTION_MAX_LENGTH} Zeichen eingeben.`,
  targetDateInvalid: 'Bitte ein gültiges Ziel-Datum wählen.',
  notFound: 'Dieses Projekt gibt es nicht.',
  stale: 'Das Projekt wurde inzwischen geändert. Die Seite zeigt jetzt den neuen Stand.',
  alreadyClosed: 'Das Projekt ist schon abgeschlossen.',
  alreadyActive: 'Das Projekt ist schon wieder offen.',
  closed: 'Das Projekt ist abgeschlossen. Bitte erst wieder öffnen oder ein anderes wählen.',
  wrongGroup: 'Nur Wiedervorlagen und freie Aufgaben können zu einem Projekt gehören.',
  filterInvalid: 'Dieses Projekt gibt es nicht. Bitte das Projekt neu wählen.',
  /** „1 von 3 erledigt“ */
  progress: (done: number, total: number) => `${done} von ${total} erledigt`,
  noTasks: 'Noch keine Aufgaben',
  noTarget: 'Ohne Ziel-Datum',
  noProject: 'Ohne Projekt',
  overdue: (days: number) => (days === 1 ? '1 Tag überfällig' : `${days} Tage überfällig`),
} as const;

/** Name eines Projekts: getrimmt, 1–80 Zeichen. */
export const taskProjectNameSchema = z
  .string({ error: TASK_PROJECT_TEXTS.nameEmpty })
  .trim()
  .min(1, TASK_PROJECT_TEXTS.nameEmpty)
  .max(TASK_PROJECT_NAME_MAX_LENGTH, TASK_PROJECT_TEXTS.nameTooLong);

/** Beschreibung eines Projekts: höchstens 2.000 Zeichen; leer → `null`. */
export const taskProjectDescriptionSchema = z.string().max(TASK_PROJECT_DESCRIPTION_MAX_LENGTH, TASK_PROJECT_TEXTS.descriptionTooLong).nullable();

/** Ziel-Datum (`YYYY-MM-DD`); `null` = ohne Ziel. */
export const taskProjectTargetDateSchema = z.string({ error: TASK_PROJECT_TEXTS.targetDateInvalid }).refine(isIsoDate, TASK_PROJECT_TEXTS.targetDateInvalid).nullable();

// ---------------------------------------------------------------------------
// Vorlagen mit Checkliste (Story 23.12, FR-161)

export const TASK_TEMPLATE_NAME_MAX_LENGTH = 60;
export const TASK_TEMPLATE_CHECKLIST_MAX_ITEMS = 50;
export const TASK_TEMPLATE_DUE_MAX_WORKDAYS = 250;

export const TASK_TEMPLATE_TEXTS = {
  nameEmpty: 'Bitte einen Namen für die Vorlage eingeben.',
  nameTooLong: `Bitte höchstens ${TASK_TEMPLATE_NAME_MAX_LENGTH} Zeichen eingeben.`,
  duplicate: (name: string) => `Die Vorlage „${name}“ gibt es schon.`,
  notFound: 'Diese Vorlage gibt es nicht.',
  unavailable: 'Diese Vorlage gibt es nicht (mehr) oder sie ist archiviert. Bitte eine andere wählen.',
  stale: 'Die Vorlage wurde inzwischen geändert. Die Liste zeigt jetzt den neuen Stand.',
  adminOnly: 'Nur der Admin kann Vorlagen ändern.',
  checklistTooMany: `Bitte höchstens ${TASK_TEMPLATE_CHECKLIST_MAX_ITEMS} Punkte eintragen.`,
  dueInvalid: `Bitte eine ganze Zahl von 0 bis ${TASK_TEMPLATE_DUE_MAX_WORKDAYS} Arbeitstagen eingeben.`,
  assigneeInvalid: 'Diese Person gibt es nicht oder sie ist gesperrt. Bitte eine aktive Person wählen.',
  /** „fällig in 5 Arbeitstagen“ (Liste, Menü). */
  dueIn: (n: number) => (n === 0 ? 'fällig am selben Tag' : n === 1 ? 'fällig in 1 Arbeitstag' : `fällig in ${n} Arbeitstagen`),
  points: (n: number) => (n === 1 ? '1 Punkt' : `${n} Punkte`),
  used: (n: number) => (n === 0 ? 'nie genutzt' : `${n}× genutzt`),
} as const;

/** Name einer Vorlage: getrimmt, 1–60 Zeichen. */
export const taskTemplateNameSchema = z
  .string({ error: TASK_TEMPLATE_TEXTS.nameEmpty })
  .trim()
  .min(1, TASK_TEMPLATE_TEXTS.nameEmpty)
  .max(TASK_TEMPLATE_NAME_MAX_LENGTH, TASK_TEMPLATE_TEXTS.nameTooLong);

/** Checkliste einer Vorlage (oder beim Anlegen): 0–50 Texte je 1–500 Zeichen. */
export const taskTemplateChecklistSchema = z.array(checklistTextSchema, { error: CHECKLIST_TEXTS.textEmpty }).max(TASK_TEMPLATE_CHECKLIST_MAX_ITEMS, TASK_TEMPLATE_TEXTS.checklistTooMany);

/** Fällig in n Arbeitstagen (0–250). */
export const taskTemplateDueSchema = z
  .number({ error: TASK_TEMPLATE_TEXTS.dueInvalid })
  .int(TASK_TEMPLATE_TEXTS.dueInvalid)
  .min(0, TASK_TEMPLATE_TEXTS.dueInvalid)
  .max(TASK_TEMPLATE_DUE_MAX_WORKDAYS, TASK_TEMPLATE_TEXTS.dueInvalid);

// ---------------------------------------------------------------------------
// Wiederkehrende Aufgaben (Story 23.11, FR-160, EXPERIENCE §4.22 „Wiederholen“)

export const taskRecurrenceStatuses = ['active', 'paused', 'ended'] as const;
export type TaskRecurrenceStatus = (typeof taskRecurrenceStatuses)[number];

/** Grenzen der Eingabe aus den Einstellungen (`task_recurrence_interval_weeks_max`, `task_recurrence_count_max`). */
export interface TaskRecurrenceLimits {
  intervalWeeksMax: number;
  countMax: number;
}
/** So viele Termine zeigt die Vorschau unter dem Menü (Entwurf A). */
export const TASK_RECURRENCE_PREVIEW_COUNT = 5;

export const TASK_RECURRENCE_TEXTS = {
  label: 'Wiederholen',
  none: 'Nicht wiederholen',
  custom: 'Eigene Regel …',
  menuTitle: (dueDate: string) => `Wiederholen · ab Fällig ${germanDate(dueDate)}`,
  noDueDate: 'Wiederholen geht erst mit einem Fälligkeitstag.',
  weekdaysEmpty: 'Bitte mindestens einen Wochentag wählen.',
  intervalInvalid: 'Bitte eine ganze Zahl ab 1 eingeben.',
  intervalTooLarge: (max: number) => `Bitte eine ganze Zahl von 1 bis ${max} eingeben.`,
  dayInvalid: 'Bitte einen Tag von 1 bis 31 wählen.',
  monthInvalid: 'Bitte einen Monat von 1 bis 12 wählen.',
  nthInvalid: 'Bitte den 1. bis 5. oder den letzten Arbeitstag wählen.',
  startInvalid: 'Bitte ein gültiges Startdatum angeben.',
  untilInvalid: 'Bitte ein Enddatum ab dem Start wählen.',
  countInvalid: 'Bitte eine Anzahl ab 1 eingeben.',
  countTooLarge: (max: number) => `Bitte eine Anzahl von 1 bis ${max} eingeben.`,
  countBelowCounted: (counted: number) => `Bisher ${counted === 1 ? 'ist schon 1 Termin' : `sind schon ${counted} Termine`} gezählt. Bitte mindestens ${counted + 1} eingeben.`,
  ruleInvalid: (reason: string) => `Diese Regel geht nicht: ${reason}`,
  notFound: 'Diese Wiederholung gibt es nicht.',
  stale: 'Die Wiederholung wurde inzwischen geändert. Bitte neu laden.',
  exists: 'Diese Aufgabe wiederholt sich schon. Bitte die Regel im Blatt ändern.',
  ended: 'Diese Wiederholung ist beendet. Bitte eine neue einstellen.',
  freeOnly: 'Wiederholen geht nur bei Aufgaben ohne Datensatz. Wiedervorlagen an einem Datensatz legt das jeweilige Modul an.',
  forbidden: 'Diese Wiederholung darfst du nicht ändern.',
  paused: 'pausiert',
  ended_: 'beendet',
  next: 'Nächste',
  noNext: '–',
  pause: 'Pausieren',
  resume: 'Fortsetzen',
  end: 'Beenden',
  showEnded: 'auch beendete',
  /** Satz unter dem Menü: „Jeden Montag, nächste: Mo 01.03.2027“. */
  sentence: (rule: string, next: string | null) => (next === null ? `${rule}, keine weiteren Termine` : `${rule}, nächste: ${next}`),
  /** Bildschirmleser und Tooltip am ↻: „Wiederholt sich: jeden Montag“. */
  marker: (rule: string) => `Wiederholt sich: ${rule.charAt(0).toLowerCase()}${rule.slice(1)}`,
  /** Vorlauf-Satz der Einstellungsseite. */
  lead: (workdays: number) =>
    workdays === 0
      ? 'Neue Aufgaben einer Wiederholung entstehen an ihrem Fälligkeitstag.'
      : `Neue Aufgaben einer Wiederholung entstehen ${workdays} ${workdays === 1 ? 'Arbeitstag' : 'Arbeitstage'} vor ihrem Fälligkeitstag.`,
  /** Kleiner Satz unter der Zeitleiste. */
  leadShort: (workdays: number) =>
    workdays === 0 ? 'Jede Aufgabe entsteht an ihrem Fälligkeitstag.' : `Jede Aufgabe entsteht ${workdays} ${workdays === 1 ? 'Arbeitstag' : 'Arbeitstage'} vorher.`,
  count: (rules: number, paused: number) => `${rules === 1 ? '1 Regel' : `${rules} Regeln`}${paused > 0 ? ` · ${paused} pausiert` : ''}`,
  /** Beispiel unter dem Vorlauf-Satz: „Beispiel: fällig Mo 08.03.2027 → entsteht Mi 03.03.2027“. */
  leadExample: (due: string, create: string) => `Beispiel: fällig ${due} → entsteht ${create}`,
  leadSaved: (workdays: number) => `Vorlauf gespeichert: ${workdays} ${workdays === 1 ? 'Arbeitstag' : 'Arbeitstage'}`,
  emptyTitle: 'Noch keine Wiederholungen',
  emptyBody: 'Eine Wiederholung stellst du im Blatt einer Aufgabe ein: Zeile „Wiederholen“.',
  emptyAction: 'Aufgabe auswählen',
  pausedNotice: (title: string) => `Wiederholung „${title}“ pausiert`,
  resumedNotice: (title: string) => `Wiederholung „${title}“ fortgesetzt`,
  endedNotice: (title: string) => `Wiederholung „${title}“ beendet`,
  savedNotice: (rule: string) => `Wiederholt sich: ${rule}`,
  removedNotice: 'Wiederholt sich nicht mehr',
  nonWorkday: { previous: 'Tag davor', next: 'Tag danach', keep: 'so lassen' },
  units: { days: 'Tage', weeks: 'Wochen', months: 'Monate', years: 'Jahre' },
  endKinds: { never: 'nie', until: 'am Datum', count: 'nach n-mal' },
} as const;

const RECURRENCE_WEEKDAY_NAMES = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'] as const;
const RECURRENCE_WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;

export const recurrenceWeekdayShort = RECURRENCE_WEEKDAY_SHORT;

/** `YYYY-MM-DD` → `TT.MM.JJJJ`. */
function germanDate(date: string): string {
  const [y, m, d] = date.split('-');
  return `${d}.${m}.${y}`;
}

/** Wochentag 1–7 (Mo = 1) eines Datums `YYYY-MM-DD` (rein, nur Kalender). */
export function isoWeekdayOf(date: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return day === 0 ? 7 : day;
}

/** „Mo 01.03.2027“ */
export function recurrenceDayLabel(date: string): string {
  return `${RECURRENCE_WEEKDAY_SHORT[isoWeekdayOf(date) - 1]} ${germanDate(date)}`;
}

/** Aufzählung „Montag“, „Montag und Donnerstag“, „Mo, Mi und Fr“. */
function weekdayList(weekdays: readonly number[]): string {
  const sorted = [...new Set(weekdays)].sort((a, b) => a - b);
  const names = sorted.map((w) => (sorted.length > 2 ? RECURRENCE_WEEKDAY_SHORT[w - 1] : RECURRENCE_WEEKDAY_NAMES[w - 1]) ?? String(w));
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} und ${names.at(-1)!}`;
}

/** Muster einer Regel (Spiegel der Kern-Typen `TaskRecurrencePattern` für Server und Web). */
export const recurrencePatternSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('daily') }),
  z.object({ kind: z.literal('workdays') }),
  z.object({
    kind: z.literal('weekly'),
    weekdays: z
      .array(z.number().int().min(1, TASK_RECURRENCE_TEXTS.weekdaysEmpty).max(7, TASK_RECURRENCE_TEXTS.weekdaysEmpty), { error: TASK_RECURRENCE_TEXTS.weekdaysEmpty })
      .min(1, TASK_RECURRENCE_TEXTS.weekdaysEmpty)
      .max(7, TASK_RECURRENCE_TEXTS.weekdaysEmpty),
    intervalWeeks: z
      .number({ error: TASK_RECURRENCE_TEXTS.intervalInvalid })
      .int(TASK_RECURRENCE_TEXTS.intervalInvalid)
      .min(1, TASK_RECURRENCE_TEXTS.intervalInvalid),
  }),
  z.object({ kind: z.literal('monthlyDay'), day: z.number({ error: TASK_RECURRENCE_TEXTS.dayInvalid }).int(TASK_RECURRENCE_TEXTS.dayInvalid).min(1, TASK_RECURRENCE_TEXTS.dayInvalid).max(31, TASK_RECURRENCE_TEXTS.dayInvalid) }),
  z.object({
    kind: z.literal('monthlyWorkday'),
    nth: z.union([z.literal('last'), z.number().int().min(1, TASK_RECURRENCE_TEXTS.nthInvalid).max(5, TASK_RECURRENCE_TEXTS.nthInvalid)], { error: TASK_RECURRENCE_TEXTS.nthInvalid }),
  }),
  z.object({
    kind: z.literal('yearly'),
    month: z.number({ error: TASK_RECURRENCE_TEXTS.monthInvalid }).int(TASK_RECURRENCE_TEXTS.monthInvalid).min(1, TASK_RECURRENCE_TEXTS.monthInvalid).max(12, TASK_RECURRENCE_TEXTS.monthInvalid),
    day: z.number({ error: TASK_RECURRENCE_TEXTS.dayInvalid }).int(TASK_RECURRENCE_TEXTS.dayInvalid).min(1, TASK_RECURRENCE_TEXTS.dayInvalid).max(31, TASK_RECURRENCE_TEXTS.dayInvalid),
  }),
]);

export const recurrenceEndSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('never') }),
  z.object({ kind: z.literal('until'), date: z.string({ error: TASK_RECURRENCE_TEXTS.untilInvalid }).refine(isIsoDate, TASK_RECURRENCE_TEXTS.untilInvalid) }),
  z.object({
    kind: z.literal('count'),
    count: z.number({ error: TASK_RECURRENCE_TEXTS.countInvalid }).int(TASK_RECURRENCE_TEXTS.countInvalid).min(1, TASK_RECURRENCE_TEXTS.countInvalid),
  }),
]);

/** Feldfehler, wenn Abstand oder Anzahl (ganze Reihe) über den Grenzen aus den Einstellungen liegen; `{}` = in Ordnung. */
export function recurrenceLimitErrors(body: Pick<RecurrenceRuleBody, 'pattern' | 'end'>, limits: TaskRecurrenceLimits): Record<string, string> {
  return {
    ...(body.pattern.kind === 'weekly' && body.pattern.intervalWeeks > limits.intervalWeeksMax ? { 'pattern.intervalWeeks': TASK_RECURRENCE_TEXTS.intervalTooLarge(limits.intervalWeeksMax) } : {}),
    ...(body.end.kind === 'count' && body.end.count > limits.countMax ? { 'end.count': TASK_RECURRENCE_TEXTS.countTooLarge(limits.countMax) } : {}),
  };
}

/** Regel ohne Start (der Start kommt aus dem Fälligkeitstag der Aufgabe). */
export const recurrenceRuleBodySchema = z.object({
  pattern: recurrencePatternSchema,
  nonWorkday: z.enum(['previous', 'next', 'keep']),
  end: recurrenceEndSchema,
});

export const recurrenceRuleSchema = recurrenceRuleBodySchema.extend({
  start: z.string({ error: TASK_RECURRENCE_TEXTS.startInvalid }).refine(isIsoDate, TASK_RECURRENCE_TEXTS.startInvalid),
});

export type RecurrencePatternInput = z.infer<typeof recurrencePatternSchema>;
/** Muster wie der Kern es führt (Wochentage nur lesend) – für Texte und Vergleiche. */
export type RecurrencePatternLike =
  | { kind: 'daily' }
  | { kind: 'workdays' }
  | { kind: 'weekly'; weekdays: readonly number[]; intervalWeeks: number }
  | { kind: 'monthlyDay'; day: number }
  | { kind: 'monthlyWorkday'; nth: number | 'last' }
  | { kind: 'yearly'; month: number; day: number };
export type RecurrenceEndLike = { kind: 'never' } | { kind: 'until'; date: string } | { kind: 'count'; count: number };
export type RecurrenceRuleBody = z.infer<typeof recurrenceRuleBodySchema>;
export type RecurrenceRuleInput = z.infer<typeof recurrenceRuleSchema>;

/** Muster in Worten: „Jeden Montag“, „Alle 2 Wochen am Donnerstag“, „Monatlich am 22.“ … (rein). */
export function recurrencePatternText(pattern: RecurrencePatternLike): string {
  switch (pattern.kind) {
    case 'daily':
      return 'Täglich';
    case 'workdays':
      return 'Jeden Arbeitstag';
    case 'weekly':
      return pattern.intervalWeeks === 1 ? `Jeden ${weekdayList(pattern.weekdays)}` : `Alle ${pattern.intervalWeeks} Wochen am ${weekdayList(pattern.weekdays)}`;
    case 'monthlyDay':
      return `Monatlich am ${pattern.day}.`;
    case 'monthlyWorkday':
      return pattern.nth === 'last' ? 'Jeden letzten Arbeitstag im Monat' : `Jeden ${pattern.nth}. Arbeitstag im Monat`;
    case 'yearly':
      return `Jährlich am ${String(pattern.day).padStart(2, '0')}.${String(pattern.month).padStart(2, '0')}.`;
  }
}

/** Regel in Worten mit Ende: „Jeden Montag“, „Täglich, bis 31.03.2027“, „Monatlich am 22., 6-mal“. */
export function recurrenceRuleText(rule: { pattern: RecurrencePatternLike; end: RecurrenceEndLike }): string {
  const base = recurrencePatternText(rule.pattern);
  if (rule.end.kind === 'until') return `${base}, bis ${germanDate(rule.end.date)}`;
  if (rule.end.kind === 'count') return `${base}, ${rule.end.count}-mal`;
  return base;
}

export interface RecurrenceSuggestion {
  key: string;
  label: string;
  pattern: RecurrencePatternInput;
}

/**
 * Vorschläge im Menü aus dem Fälligkeitstag (Entwurf A): Täglich, Jeden Arbeitstag, Wöchentlich am <Tag>, Monatlich am
 * <n>., Letzter Arbeitstag im Monat, Jährlich am <TT.MM.>. Nur Muster – die Termine rechnet der Server mit dem Kern.
 */
export function recurrenceSuggestions(dueDate: string): RecurrenceSuggestion[] {
  const weekday = isoWeekdayOf(dueDate);
  const [, m, d] = dueDate.split('-').map(Number) as [number, number, number];
  return [
    { key: 'daily', label: 'Täglich', pattern: { kind: 'daily' } },
    { key: 'workdays', label: 'Jeden Arbeitstag', pattern: { kind: 'workdays' } },
    { key: 'weekly', label: `Wöchentlich am ${RECURRENCE_WEEKDAY_NAMES[weekday - 1]}`, pattern: { kind: 'weekly', weekdays: [weekday], intervalWeeks: 1 } },
    { key: 'monthlyDay', label: `Monatlich am ${d}.`, pattern: { kind: 'monthlyDay', day: d } },
    { key: 'monthlyWorkday', label: 'Letzter Arbeitstag im Monat', pattern: { kind: 'monthlyWorkday', nth: 'last' } },
    { key: 'yearly', label: `Jährlich am ${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.`, pattern: { kind: 'yearly', month: m, day: d } },
  ];
}

/** Gleiches Muster (für das ✓ im Menü). */
export function samePattern(a: RecurrencePatternLike, b: RecurrencePatternLike): boolean {
  switch (a.kind) {
    case 'daily':
    case 'workdays':
      return b.kind === a.kind;
    case 'weekly':
      return b.kind === 'weekly' && a.intervalWeeks === b.intervalWeeks && [...new Set(a.weekdays)].sort().join(',') === [...new Set(b.weekdays)].sort().join(',');
    case 'monthlyDay':
      return b.kind === 'monthlyDay' && a.day === b.day;
    case 'monthlyWorkday':
      return b.kind === 'monthlyWorkday' && a.nth === b.nth;
    case 'yearly':
      return b.kind === 'yearly' && a.month === b.month && a.day === b.day;
  }
}

// ---------------------------------------------------------------------------
// Klärfälle (Story 2.3, AD-17)

export const caseStatuses = ['open', 'resolved'] as const;
export type CaseStatus = (typeof caseStatuses)[number];

/** Ansichten der Aufgabenseite (EXPERIENCE §4.2): eigene Aufgaben oder die des ganzen Teams. */
export const taskViews = ['mine', 'team'] as const;
export type TaskView = (typeof taskViews)[number];

export const CASE_TITLE_MAX_LENGTH = 200;
export const CASE_DETAIL_MAX_LENGTH = 500;

export const CASE_TEXTS = {
  reasonUnknown: 'Diesen Grund für einen Klärfall gibt es nicht.',
  entityTypeInvalid: 'Die Art des Datensatzes ist ungültig.',
  entityIdMissing: 'Die Kennung des Datensatzes fehlt.',
  titleEmpty: 'Bitte einen Titel für den Klärfall angeben.',
  detailEmpty: 'Bitte den Grund des Klärfalls als Satz angeben.',
  ownerOnly: 'Nur der Besitzer des Klärfalls darf ihn schließen.',
  notFound: 'Diesen Klärfall gibt es nicht.',
  opened: (detail: string) => `Klärfall eröffnet: ${detail}`,
  raisedAgain: (occurrence: number, detail: string) => `Erneut aufgetreten (${occurrence}. Mal): ${detail}`,
  resolved: (resolution: string) => `Gelöst: ${resolution}`,
  sideEffectTitle: (counterpart: string) => `Übertragung an ${counterpart} endgültig fehlgeschlagen`,
  sideEffectResolution: (counterpart: string) => `Der Vorgang an ${counterpart} lief durch.`,
  alarmTitle: (title: string, probe: boolean) => `Alarm: ${title}${probe ? ' (Probe)' : ''}`,
  /** Zugänge-Wächter (Story 1.85): „Zugang fehlgeschlagen: Shopware“, „Zugang läuft ab: eBay – Refresh-Token …“. */
  credentialFailedTitle: (label: string) => `Zugang fehlgeschlagen: ${label}`.slice(0, 200),
  credentialExpiringTitle: (label: string) => `Zugang läuft ab: ${label}`.slice(0, 200),
  credentialOkResolution: (label: string) => `Die Verbindung mit ${label} ist wieder in Ordnung.`,
  credentialRemovedResolution: 'Der Zugang wurde entfernt.',
  /** Abweichung nach einer Wiederherstellung (Story 1.13), z. B. „Nach Wiederherstellung prüfen: Vorgang an Verbindungstest“. */
  restoreTitle: (subject: string) => `Nach Wiederherstellung prüfen: ${subject}`,
  restoreResolution: 'Nach der Wiederherstellung hat der Admin wieder live geschaltet.',
  restoreHoldResolution: (decision: 'executed' | 'send' | 'cancel') =>
    decision === 'executed'
      ? 'Der Admin hat entschieden: Der Vorgang war bereits ausgeführt (nicht erneut gesendet).'
      : decision === 'send'
        ? 'Der Admin hat entschieden: Der Vorgang wird gesendet.'
        : 'Der Admin hat entschieden: Der Vorgang wird nicht gesendet (abgebrochen).',
  /** Grund plus Notfall-Handbuch; auf 500 Zeichen gekürzt (der Grund kommt aus dem Prüflauf). */
  alarmDetail: (detail: string, runbook: string) => {
    const suffix = ` Notfall-Handbuch: ${runbook}`;
    return `${detail.slice(0, 500 - suffix.length)}${suffix}`;
  },
} as const;

export * from './case-groups.ts';

// Story 23.20: Aufgabe aus Notiz (FR-166) – reiner, browser-sicherer Kern; die Web-App liest ihn über diesen Unterpfad.
export { buildNoteTaskDraft, NOTE_TASK_TITLE_MAX, NOTE_TASK_VERSION, type NoteTaskDraft, type NoteTaskDraftInput } from './core/note-task.ts';
export type { QuickEntryPerson } from './core/task-quick-entry.ts';
